import { useEffect, useState } from 'react'
import { probeSafe, type SafeProbe } from '../lib/probeSafe.js'

export type ProbeState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; result: SafeProbe }

/**
 * Runs the on-chain probe for an address whenever it (or the Guardian addr)
 * changes. The Guardian arrives a tick after `/v1/health` resolves; without
 * it, the probe can detect the Safe's modules but can't tell which one
 * (if any) is SafeWard's. So we re-fire when guardianAddress lands.
 */
export function useSafeProbe(
  address: string | null,
  guardianAddress: string | null,
): ProbeState {
  const [state, setState] = useState<ProbeState>({ status: 'idle' })

  useEffect(() => {
    if (!address) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    probeSafe(address, guardianAddress)
      .then((result) => {
        if (!cancelled) setState({ status: 'done', result })
      })
      .catch((err: unknown) => {
        // probeSafe is meant to swallow RPC errors and resolve with kind:
        // 'unreachable', but if anything unexpected throws we mustn't leave
        // the dashboard wedged on the loading spinner — App.tsx only
        // renders the dashboard when probe.status === 'done'.
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        setState({
          status: 'done',
          result: { kind: 'unreachable', error: message },
        })
      })
    return () => {
      cancelled = true
    }
  }, [address, guardianAddress])

  return state
}
